import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useApiContext } from '@/contexts/apiContext';
import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import ErrorMessage from '@/components/errorMessage';
import SettingsSection from '@/components/entities/configuration/settingsSection';
import SettingsValidator from '@/components/entities/settingsValidators';
import { useTranslation } from 'react-i18next';

type Props = {};

const settingsValidator = new SettingsValidator();
const NewDataSourceInstance = ({}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [dataSourceDefs, setDataSourceDefs] = useState<any[]>([]);
  const [dataSourceDef, setDataSourceDef] = useState<any>();
  const [dataSourceDefId, setDataSourceDefId] = useState<any>();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [dataSource, setDataSource] = useState<any>();

  const [errors, setErrors] = useState({} as any);
  const [error, setError] = useState<any>();
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    const getDataSourceDefs = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getDataSourceDefs();
        setDataSourceDefs(response);

        const queryDefId = searchParams.get('defId');
        if (queryDefId && response.some((dsd: any) => dsd.id === queryDefId)) {
          setDataSourceDefId(queryDefId);
          setDataSourceDef(response.find((dsd: any) => dsd.id === queryDefId));
          const instance = await apiContext.getNewDataSourceInstance(queryDefId);
          setDataSource(instance);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    getDataSourceDefs();
  }, [searchParams]);

  const onDefChange = async (dataSourceDefId: string) => {
    setDataSourceDefId(dataSourceDefId);
    setDataSourceDef(dataSourceDefs.find((dsd) => dsd.id === dataSourceDefId));
    if (dataSourceDefId) {
      const dataSource = await apiContext.getNewDataSourceInstance(dataSourceDefId);
      setDataSource(dataSource);
    } else {
      setDataSource(null);
    }
  };

  const onSettingChange = (settingName: string, value: any) => {
    setDataSource({ ...dataSource, settings: { ...dataSource.settings, [settingName]: value } });
  };

  const submitEntity = async () => {
    const errors = {} as any;
    let hasError = false;

    if (!dataSourceDefId) {
      errors['dataSourceDefId'] = 'Required';
      hasError = true;
    }
    if (!name) {
      errors['name'] = 'Required';
      hasError = true;
    }

    const [settingErrors = {}, hasSettingsError = false] = settingsValidator.validateSettings(
      dataSourceDef.settingDefs,
      dataSource.settings,
      t,
    );

    hasError = hasError || hasSettingsError;
    setErrors({ ...errors, ...settingErrors });
    setHasErrors(hasError);

    if (!hasError) {
      const { id, tenantId, entityType, entityDefId, settings = {} } = dataSource;
      try {
        setLoading(true);
        const response = await apiContext.createDataSourceInstance({
          id,
          tenantId,
          entityType,
          entityDefId,
          name,
          description,
          settings,
        });
        navigate(`/dataSourceInstance/${dataSourceDefId}/${response.id}`, {
          state: { from: '/instances?tab=dataSourceInstances' },
        });
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const close = () => navigate('/instances?tab=dataSourceInstances');

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          New Data Source
        </Typography>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        {error && <ErrorMessage error={error} />}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SelectField
              label="Data source type"
              required
              options={dataSourceDefs.map((dsd) => ({ value: dsd.id, label: dsd.name }))}
              value={dataSourceDefId}
              autoFocus
              onChange={onDefChange}
              error={errors.dataSourceDefId}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <TextField label="Name" value={name} required error={errors.name} onChange={setName} type="text" />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <TextField
              label="Description"
              value={description}
              multiline
              maxRows={2}
              error={errors.description}
              onChange={setDescription}
            />
          </Grid>
        </Grid>

        {dataSource && dataSourceDef && (
          <Box sx={{ mt: 2 }}>
            {dataSourceDef?.settingDefs
              .filter((section: any) => !section.system)
              .map((section: any) => (
                <SettingsSection
                  key={section.sectionName}
                  entityDefId={dataSourceDef.id}
                  settings={dataSource.settings}
                  settingsSection={section}
                  errors={errors}
                  onChange={onSettingChange}
                />
              ))}
            {hasErrors && <ErrorMessage error={{ message: 'Please enter valid values in the fields with error.' }} />}
          </Box>
        )}
      </Box>

      {/* Action bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Button size="small" variant="text" onClick={close} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          loading={loading}
          size="small"
          color="secondary"
          variant="contained"
          onClick={submitEntity}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default NewDataSourceInstance;
